import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { IMAGE_CONFIG, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';

import { GALLERY_CONFIG, GalleryConfig } from 'ng-gallery';
import { ToastrModule } from 'ngx-toastr';
import { AuthModule } from '@auth0/auth0-angular';
import { NgxDaterangepickerBootstrapModule } from 'ngx-daterangepicker-bootstrap';

import { AppComponent } from './app.component';
import { LoaderComponent } from './components/shared/loader/loader.component';
import { HttpDefaultInterceptor } from './utils/interceptor/default.interceptor';
import { LoadingInterceptor } from './utils/interceptor/loading.service';
import { HttpTokenInterceptor } from './utils/interceptor/token.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { SnappstayState } from './state/snappstay.state';
import { environment } from 'src/environments/environment';
import { StoreModule } from '@ngrx/store';
import { rootReducer } from './store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ListingsEffects } from './store/listings/listings-effects';
import { EffectsModule } from '@ngrx/effects';
import { AuthenticationEffects } from './store/auth/authentication.effects';
import { SharedStoreEffects } from './store/shared/effects';
import { EventLogStoreEffects } from './store/event-log-store/effects';
import { UsersStoreEffects } from './store/users/effects';
import { ErrorInterceptor } from './utils/interceptor/error.interceptors';
import { GuestWishlistEffects } from './store/wishlist/effects';
import { PayoutsEffects } from './store/payouts/payouts.effects';

const authRedirectUri = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:4200';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatSnackBarModule,
    LoaderComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgxsModule.forRoot([SnappstayState]),
    NgxsStoragePluginModule.forRoot({
      storage: StorageOption.LocalStorage,
      keys: [SnappstayState],
    }),
    NgxsLoggerPluginModule.forRoot({ disabled: true }),
    ToastrModule.forRoot(),
    NgxDaterangepickerBootstrapModule.forRoot(),
    AuthModule.forRoot({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri: authRedirectUri,
      },
    }),
    AppRoutingModule,
    StoreModule.forRoot(rootReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    EffectsModule.forRoot([
      ListingsEffects,
      AuthenticationEffects,
      SharedStoreEffects,
      EventLogStoreEffects,
      UsersStoreEffects,
      GuestWishlistEffects,
      PayoutsEffects,
    ]),
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: GALLERY_CONFIG,
      useValue: {
        imageSize: 'cover',
      } as GalleryConfig,
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpDefaultInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
